import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { generatePPTFromText } from "./action";
import { useDispatch } from "react-redux";
import { useAppDispatch } from "src/hooks/redux";

interface TemplateQuantAgentProps {
  response: any;
}

export default function TemplateQuantAgent({ response }: TemplateQuantAgentProps) {
  const deck = response?.deck_structure || [];
   const dispatch = useAppDispatch();
    

  // UI State
  const [loading, setLoading] = useState(false);
  const [fileBlob, setFileBlob] = useState<Blob | null>(null);

  // HANDLE GENERATE BUTTON
  const handleGenerate = async () => {
    try {
      setLoading(true);

      // convert deck to string for API body
      const rawText = JSON.stringify(response);

      const result: any = await dispatch(generatePPTFromText(rawText));
    
    

      if (result.payload) {
        setFileBlob(result.payload); // Set blob for download
      }

      setLoading(false);
    } catch (err) {
      console.log("Error:", err);
      setLoading(false);
    }
  };

  // DOWNLOAD FILE
  const handleDownload = () => {
    if (!fileBlob) return;

    const url = window.URL.createObjectURL(fileBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Generated_Deck.pptx";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="h-[90vh] max-w-xl border rounded-xl border-gray-300 overflow-y-auto pn_scroller">
      {/* Header */}
      <div className="lex justify-between items-center p-3 border-b border-gray-300">

        <div className="f">
           <h1 className="text-lg font-bold text-gray-900 mb-1">
            Quant Deck Structure
          </h1>
          <p className="text-gray-600 text-sm">
            Auto-generated presentation-ready slide structure with insights.
          </p>
        </div>
      
           <div className="  mt-2">

            {/* Before generation */}
            {!fileBlob && (
              <button
                onClick={handleGenerate}
                disabled={loading}
                className={`px-2 py-1 rounded-md text-sm font-semibold 
                  ${loading ? "bg-gray-300 text-gray-600" : "bg-blue-600 text-white hover:bg-blue-700"}
                `}
              >
                {loading ? "Generating..." : "Generate Deck"}
              </button>
            )}

            {/* After generation */}
            {fileBlob && (
              <button
                onClick={handleDownload}
                className="px-2 py-1 bg-green-600 text-white rounded-md text-sm font-semibold hover:bg-green-700"
              >
                Download Deck
              </button>
            )}

          </div>
      </div>

      {/* Slides */}
      <div className="bg-white rounded-lg shadow-lg px-3 space-y-2 mt-4 pb-4 w-full">
        {deck.length === 0 ? (
          <p className="text-sm text-gray-500">No deck data found.</p>
        ) : (
          deck.map((slide: any) => (
            <AccordionItem
              key={slide.slide_number}
              title={`${slide.slide_number}. ${slide.title} (${slide.type})`}
            >
              <SlideContent slide={slide} />
            </AccordionItem>
          ))
        )}
      

      </div>
    </div>
  );
}

/* ---------------------------------------------
   Slide Content
--------------------------------------------- */
function SlideContent({ slide }: any) {
  return (
    <div className="space-y-4">
      {/* Insights */}
      <div>
        <p className="text-sm font-semibold text-blue-900 mb-1">Insights:</p>

        {slide.insights?.length === 0 ? (
          <p className="text-sm text-gray-500">No insights available.</p>
        ) : (
          <div className="space-y-2">
            {slide.insights.map((ins: any) => (
              <div
                key={ins.id}
                className="border rounded-lg p-3 bg-blue-50 border-blue-200"
              >
                <p className="font-semibold text-blue-900">{ins.headline}</p>
                <p className="text-sm text-blue-800 mt-1">{ins.stats}</p>

                <p className="text-xs text-gray-700 mt-2 whitespace-pre-line">
                  {ins.implications}
                </p>

                {/* Tags */}
                <div className="mt-2 flex gap-2 flex-wrap">
                  {ins.tags?.map((tag: string, index: number) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs bg-gray-200 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Charts */}
      {/* <div>
        <p className="text-sm font-semibold text-green-900 mb-1">Charts:</p>

        {slide.charts?.length === 0 ? (
          <p className="text-sm text-gray-500">No charts added.</p>
        ) : (
          slide.charts.map((chart: any, idx: number) => (
            <div
              key={idx}
              className="p-3 border bg-green-50 rounded border-green-200"
            >
              <p className="text-sm text-green-900 font-medium">
                {chart.chart_type}
              </p>
            </div>
          ))
        )}
      </div> */}
    </div>
  );
}

/* ---------------------------------------------
   Accordion Item
--------------------------------------------- */
function AccordionItem({ title, children }: any) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border bg-gray-100 border-gray-300 p-3 rounded-xl">
      {/* Header */}
      <button
        className="w-full text-left flex justify-between items-center group transition-colors hover:text-blue-600 text-base font-semibold"
        onClick={() => setOpen(!open)}
      >
        <span className="flex-1">{title}</span>
        <span
          className={`ml-4 flex-shrink-0 text-xl transition-transform ${open ? "rotate-90" : "rotate-0"
            }`}
        >
          {open ? (
            <ChevronDown className="w-3 h-3 text-slate-500" />
          ) : (
            <ChevronRight className="w-3 h-3 text-slate-500" />
          )}
        </span>
      </button>

      {/* Content */}
      {open && <div className="mt-3 text-gray-700">{children}</div>}
    </div>
  );
}